import {
    getLinksUploadArtwork,
    uploadTempArtwork,
    uploadDesignLibrary,
} from '../../../../services/ArtworkTemplateServices'

/**
 * Session cache key for a file (same file => same key within session).
 * @param {File} file
 * @returns {string}
 */
function getFileSessionKey(file) {
    return `${file.name}-${file.size}-${file.lastModified}`
}

/** Module-level session cache so it survives component remounts. */
const sessionUploadCache = new Map()

/**
 * Fetches a single presigned URL for S3 upload.
 * @returns {Promise<string>} Presigned URL
 */
async function getPresignedUrl() {
    const {success, data} = await getLinksUploadArtwork({limit: 1})
    if (!success || !data?.[0]) {
        throw new Error('Failed to get upload URL')
    }
    return data[0]
}

/**
 * Uploads a file to S3 using presigned URL, then registers it via uploadDesignLibrary.
 * @param {File} file
 * @returns {Promise<{ link: string, name: string }>}
 */
async function uploadFile(file) {
    const presignedUrl = await getPresignedUrl()
    const link = presignedUrl.split('?')[0]
    const contentType = file.type || 'image/png'

    await uploadTempArtwork(presignedUrl, file, contentType)

    const {success, data, message} = await uploadDesignLibrary({
        link,
        name: file.name,
    })
    if (!success) {
        throw new Error(message || 'Failed to save image to library')
    }
    return {link, name: file.name, ...data}
}

/**
 * Centralized image persistence service.
 * - Session cache: same file (name+size+lastModified) returns cached result.
 * - Silent background behavior: returns null on error, logs to console.error.
 */
export const ImagePersistenceService = {
    /**
     * Upload a file to the design library with session caching.
     * If the same file was already uploaded in this session, the cached result is returned.
     * On error, logs to console.error and returns null (silent / background behavior).
     * @param {File} file
     * @returns {Promise<{ link: string, name?: string } | null>}
     */
    async persistImage(file) {
        if (!file) return null
        const key = getFileSessionKey(file)
        const cached = sessionUploadCache.get(key)
        if (cached) return cached
        try {
            const result = await uploadFile(file)
            if (result?.link) sessionUploadCache.set(key, result)
            return result
        } catch (err) {
            console.error('[ImagePersistenceService] persistImage failed:', err)
            return null
        }
    },

    /**
     * Upload a file without session caching. Throws on error.
     * @param {File} file
     * @returns {Promise<{ link: string, name: string }>}
     */
    uploadFile,

    /** Exposed for testing / advanced usage. */
    _cache: sessionUploadCache,
    _getFileSessionKey: getFileSessionKey,
}
